import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { messageAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Bell, 
  Archive,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Clock
} from 'lucide-react';

export function AlertsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState(searchParams.get('status') || 'all');
  const pageSize = 10;

  const loadMessages = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        type: 'alert',
        page,
        size: pageSize,
        status: status === 'all' ? undefined : status
      };
      const response = await messageAPI.getMessages(params);
      setMessages(response.data.messages || []);
      setSummary(response.data.summary);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, [page, status]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const handleStatusChange = (newStatus) => {
    setStatus(newStatus);
    setPage(1);
    setSearchParams(newStatus === 'all' ? {} : { status: newStatus });
  };

  const handleArchive = async (e, messageId) => {
    e.stopPropagation();
    try {
      await messageAPI.updateMessage(messageId, { archived: true });
      toast.success('Alert archived');
      loadMessages();
    } catch (error) {
      toast.error('Failed to archive alert');
    }
  };

  const getAlertIcon = (source) => {
    switch (source.toLowerCase()) {
      case 'security':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'system':
        return <Clock className="w-5 h-5 text-blue-500" />;
      case 'billing':
        return <Bell className="w-5 h-5 text-accent" />;
      default:
        return <Bell className="w-5 h-5 text-accent" />;
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="p-8 space-y-6" data-testid="alerts-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
            <Bell className="w-8 h-8 text-accent" />
            Alerts
          </h1>
          <p className="text-muted-foreground mt-1">
            Important notifications and alerts
          </p>
        </div>
        <Button variant="outline" onClick={loadMessages} disabled={loading} data-testid="refresh-alerts-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card 
            className={`cursor-pointer transition-all ${status === 'all' ? 'ring-2 ring-accent' : 'hover:shadow-md'}`}
            onClick={() => handleStatusChange('all')}
          >
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{summary.total}</p>
              <p className="text-sm text-muted-foreground">All Alerts</p>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer transition-all ${status === 'unread' ? 'ring-2 ring-accent' : 'hover:shadow-md'}`}
            onClick={() => handleStatusChange('unread')}
          >
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-accent">{summary.unread}</p>
              <p className="text-sm text-muted-foreground">Unread</p>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer transition-all ${status === 'read' ? 'ring-2 ring-accent' : 'hover:shadow-md'}`}
            onClick={() => handleStatusChange('read')}
          >
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-500">{summary.read}</p>
              <p className="text-sm text-muted-foreground">Acknowledged</p>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer transition-all ${status === 'archived' ? 'ring-2 ring-accent' : 'hover:shadow-md'}`}
            onClick={() => handleStatusChange('archived')}
          >
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-muted-foreground">{summary.archived}</p>
              <p className="text-sm text-muted-foreground">Archived</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Alerts List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} className="h-20" />
              ))}
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500 opacity-50" />
              <p className="text-lg">All caught up!</p>
              <p className="text-sm mt-2">
                {status !== 'all' ? 'No alerts in this category' : 'You have no pending alerts'}
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {messages.map((message) => (
                <div
                  key={message.message_id}
                  className={`p-4 cursor-pointer transition-all duration-200 hover:bg-accent/5 ${
                    !message.read ? 'bg-accent/5 border-l-4 border-accent' : 'border-l-4 border-transparent'
                  }`}
                  onClick={() => navigate(`/messages/${message.message_id}`)}
                  data-testid={`alert-item-${message.message_id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                        {getAlertIcon(message.source)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className={`font-medium truncate ${!message.read ? 'font-semibold' : ''}`}>
                            {message.subject}
                          </h4>
                          {!message.read && (
                            <Badge className="bg-accent text-accent-foreground text-xs flex-shrink-0">
                              New Alert
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="capitalize">{message.source}</span>
                          <span>{formatDate(message.sent_date)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {!message.archived && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => handleArchive(e, message.message_id)}
                          title="Archive"
                          data-testid={`archive-alert-btn-${message.message_id}`}
                        >
                          <Archive className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, total)} of {total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm px-2">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
